import { useState, useEffect } from 'react'
import { useForm, useFieldArray } from 'react-hook-form'
import { useParams, Link } from 'react-router-dom'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { useAuth } from '../../utils/auth'
import { tenantApi } from '../../utils/api'
import { useToast } from '../../components/Toast'
import {
  Calendar,
  Users,
  CheckCircle,
  ClipboardList,
  Pause,
  Lock,
  Percent,
  HelpCircle,
  Archive,
  CalendarX,
  Ticket,
} from 'lucide-react'

interface Camp {
  id: number
  name: string
  description: string
  startDate: string
  endDate: string
  ageMin: number
  ageMax: number
  maxSpots: number
  spotsTaken: number
  status: string
  registrationStatus: string
  waitlistEnabled: boolean
  waitlistMessage: string
  siblingDiscountEnabled: boolean
  siblingDiscountAmount: number
  siblingDiscountType: string
}

interface PricingItem {
  id: number
  name: string
  description: string
  amount: number
  campId: number | null
  itemType: string
  isRequired: boolean
}

// Child schema
const childSchema = z.object({
  childFullName: z.string().min(2, 'Name is required'),
  childDob: z.string().min(1, 'Date of birth is required'),
  walkHomeAlone: z.enum(['yes', 'no'], { required_error: 'Required' }),
  hasMedicalConditions: z.enum(['yes', 'no'], { required_error: 'Required' }),
  medicalConditionsDetails: z.string().optional(),
  hasAdditionalNeeds: z.enum(['yes', 'no'], { required_error: 'Required' }),
  additionalNeedsDetails: z.string().optional(),
  hasAllergies: z.enum(['yes', 'no'], { required_error: 'Required' }),
  allergiesDetails: z.string().optional(),
  hasMedication: z.enum(['yes', 'no'], { required_error: 'Required' }),
  medicationDetails: z.string().optional(),
  hasFurtherInfo: z.enum(['yes', 'no'], { required_error: 'Required' }),
  furtherInfoDetails: z.string().optional(),
})

// Multi-child form schema
const multiChildFormSchema = z.object({
  email: z.string().email('Valid email required'),
  parentFullName: z.string().min(2, 'Parent name is required'),
  address: z.string().optional(),
  phone: z.string().min(10, 'Valid phone number required'),
  emergency1Name: z.string().min(2, 'Emergency contact required'),
  emergency1Phone: z.string().min(10, 'Valid phone required'),
  emergency1Relationship: z.string().min(1, 'Relationship required'),
  emergency2Name: z.string().optional(),
  emergency2Phone: z.string().optional(),
  emergency2Relationship: z.string().optional(),
  authorisedCollectors: z.string().optional(),
  children: z.array(childSchema).min(1, 'At least one child is required'),
  permissionPhotos: z.boolean().refine(v => v, 'Required'),
  permissionHealth: z.boolean().refine(v => v, 'Required'),
  permissionActivities: z.boolean().refine(v => v, 'Required'),
  permissionLocations: z.boolean().refine(v => v, 'Required'),
  permissionMeals: z.boolean().refine(v => v, 'Required'),
  permissionBathroom: z.boolean().refine(v => v, 'Required'),
  permissionFirstAid: z.boolean().refine(v => v, 'Required'),
  permissionEquipment: z.boolean().refine(v => v, 'Required'),
  permissionAppWaiver: z.boolean().refine(v => v, 'Required'),
})

type MultiChildFormData = z.infer<typeof multiChildFormSchema>
type ChildFormData = z.infer<typeof childSchema>

const defaultChild: ChildFormData = {
  childFullName: '',
  childDob: '',
  walkHomeAlone: 'no',
  hasMedicalConditions: 'no',
  medicalConditionsDetails: '',
  hasAdditionalNeeds: 'no',
  additionalNeedsDetails: '',
  hasAllergies: 'no',
  allergiesDetails: '',
  hasMedication: 'no',
  medicationDetails: '',
  hasFurtherInfo: 'no',
  furtherInfoDetails: '',
}

type UnavailableReason = 'not_found' | 'archived' | 'past' | 'paused' | 'closed' | 'full'

export default function TenantRegister() {
  const { slug, campId } = useParams<{ slug?: string; campId?: string }>()
  const { tenant, loading: authLoading } = useAuth()
  const [camps, setCamps] = useState<Camp[]>([])
  const [selectedCamp, setSelectedCamp] = useState<Camp | null>(null)
  const [unavailableCamp, setUnavailableCamp] = useState<{ camp: Camp | null; reason: UnavailableReason } | null>(null)
  const [pricingItems, setPricingItems] = useState<PricingItem[]>([])
  const [selectedItems, setSelectedItems] = useState<{ [key: number]: boolean }>({})
  const [loading, setLoading] = useState(true)
  const [submitted, setSubmitted] = useState(false)
  const [totalAmount, setTotalAmount] = useState(0)
  const [siblingDiscount, setSiblingDiscount] = useState(0)
  const [clientSecret, setClientSecret] = useState<string>('')
  const [step, setStep] = useState<'form' | 'payment'>('form')
  const { showToast } = useToast()

  const basePath = slug ? `/t/${slug}` : ''

  // Initialize Stripe with tenant's key (fallback to platform key)
  const stripePromise = tenant?.stripePublishableKey 
    ? loadStripe(tenant.stripePublishableKey)
    : null

  // Load saved form data from localStorage
  const getSavedFormData = () => {
    try {
      const saved = localStorage.getItem(`registration_form_draft_${tenant?.id || 'default'}`)
      if (saved) {
        const parsed = JSON.parse(saved)
        if (parsed._savedAt && Date.now() - parsed._savedAt < 24 * 60 * 60 * 1000) {
          const { _savedAt, ...formData } = parsed
          return formData
        }
      }
    } catch (e) {
      console.error('Failed to load saved form data:', e)
    }
    return null
  }

  const savedData = getSavedFormData()

  const {
    register,
    control,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<MultiChildFormData>({
    resolver: zodResolver(multiChildFormSchema),
    defaultValues: savedData || {
      children: [defaultChild],
      permissionPhotos: false,
      permissionHealth: false,
      permissionActivities: false,
      permissionLocations: false,
      permissionMeals: false,
      permissionBathroom: false,
      permissionFirstAid: false,
      permissionEquipment: false,
      permissionAppWaiver: false,
    },
  })

  // Auto-save form data to localStorage
  const formValues = watch()
  useEffect(() => {
    if (!tenant) return
    const timeoutId = setTimeout(() => {
      try {
        localStorage.setItem(`registration_form_draft_${tenant.id}`, JSON.stringify({
          ...formValues,
          _savedAt: Date.now()
        }))
      } catch (e) {
        // Ignore storage errors
      }
    }, 1000)
    return () => clearTimeout(timeoutId)
  }, [formValues, tenant])

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'children',
  })

  // Load camps and pricing
  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const [campsData, pricingData, allCampsData] = await Promise.all([
        tenantApi.getCamps(),
        tenantApi.getPricing(),
        campId ? tenantApi.getCamps(true) : Promise.resolve({ camps: [] }),
      ])

      const today = new Date()
      today.setHours(0, 0, 0, 0)
      
      const activeCamps = (campsData.camps || []).filter((c: Camp) => {
        const endDate = new Date(c.endDate)
        endDate.setHours(23, 59, 59, 999)
        return c.status === 'active' && endDate >= today && c.registrationStatus === 'open'
      })
      
      setCamps(activeCamps)
      setPricingItems(pricingData.items || [])
      
      if (campId) {
        const camp = activeCamps.find((c: Camp) => c.id === parseInt(campId))
        if (camp) {
          setSelectedCamp(camp)
          const requiredItems: { [key: number]: boolean } = {}
          pricingData.items
            ?.filter((item: PricingItem) => item.campId === camp.id && item.isRequired)
            .forEach((item: PricingItem) => { requiredItems[item.id] = true })
          setSelectedItems(requiredItems)
        } else {
          const allCamps = allCampsData?.camps || []
          const requestedCamp = allCamps.find((c: Camp) => c.id === parseInt(campId))
          
          if (!requestedCamp) {
            setUnavailableCamp({ camp: null, reason: 'not_found' })
          } else {
            const endDate = new Date(requestedCamp.endDate)
            endDate.setHours(23, 59, 59, 999)
            const spotsLeft = requestedCamp.maxSpots - requestedCamp.spotsTaken
            
            if (requestedCamp.status === 'archived') {
              setUnavailableCamp({ camp: requestedCamp, reason: 'archived' })
            } else if (endDate < today) {
              setUnavailableCamp({ camp: requestedCamp, reason: 'past' })
            } else if (requestedCamp.registrationStatus === 'paused') {
              setUnavailableCamp({ camp: requestedCamp, reason: 'paused' })
            } else if (requestedCamp.registrationStatus === 'closed') {
              setUnavailableCamp({ camp: requestedCamp, reason: 'closed' })
            } else if (spotsLeft <= 0 && !requestedCamp.waitlistEnabled) {
              setUnavailableCamp({ camp: requestedCamp, reason: 'full' })
            } else {
              setUnavailableCamp({ camp: requestedCamp, reason: 'not_found' })
            }
          }
        }
      }
      
      setLoading(false)
    } catch (err) {
      console.error('Failed to load data:', err)
      setLoading(false)
    }
  }
  
  // Auto-select required items when camp changes
  useEffect(() => {
    if (selectedCamp) {
      const requiredItems: { [key: number]: boolean } = {}
      pricingItems
        .filter(item => item.campId === selectedCamp.id && item.isRequired)
        .forEach(item => { requiredItems[item.id] = true })
      setSelectedItems(prev => ({
        ...Object.fromEntries(
          Object.entries(prev).filter(([id]) => 
            pricingItems.find(item => item.id === parseInt(id))?.campId === selectedCamp.id
          )
        ),
        ...requiredItems
      }))
    }
  }, [selectedCamp, pricingItems])

  // Calculate total - pricing per child with sibling discount
  useEffect(() => {
    if (!selectedCamp) {
      setTotalAmount(0)
      setSiblingDiscount(0)
      return
    }

    const campPricingItems = pricingItems.filter(item => item.campId === selectedCamp.id)

    let itemTotal = 0
    campPricingItems.forEach((item) => {
      if (selectedItems[item.id]) {
        itemTotal += item.amount
      }
    })

    const numChildren = fields.length
    let total = itemTotal * numChildren
    
    let discount = 0
    if (numChildren >= 2 && selectedCamp.siblingDiscountEnabled) {
      const additionalChildren = numChildren - 1
      
      if (selectedCamp.siblingDiscountType === 'percentage') {
        discount = (itemTotal * (selectedCamp.siblingDiscountAmount / 100)) * additionalChildren
      } else {
        discount = selectedCamp.siblingDiscountAmount * additionalChildren
      }
    }
    
    setSiblingDiscount(discount)
    setTotalAmount(Math.max(total - discount, 0))
  }, [selectedCamp, selectedItems, pricingItems, fields.length])

  const currency = tenant?.currency === 'usd' ? '$' : 'AED'

  const onSubmitForm = async (data: MultiChildFormData) => {
    if (!selectedCamp) {
      showToast('Please select a camp', 'warning')
      return
    }

    const spotsNeeded = data.children.length
    const spotsLeft = selectedCamp.maxSpots - selectedCamp.spotsTaken
    if (spotsNeeded > spotsLeft && !selectedCamp.waitlistEnabled) {
      showToast(`Only ${spotsLeft} spots available, but you're registering ${spotsNeeded} children.`, 'error')
      return
    }

    try {
      const isWaitlist = selectedCamp.maxSpots - selectedCamp.spotsTaken <= 0 && selectedCamp.waitlistEnabled
      
      if (totalAmount === 0 || isWaitlist) {
        await tenantApi.submitRegistration({
          ...data,
          campId: selectedCamp.id,
          selectedItems: Object.keys(selectedItems).filter(k => selectedItems[parseInt(k)]).map(k => parseInt(k)),
          totalAmount: isWaitlist ? 0 : 0,
          paymentStatus: isWaitlist ? 'pending' : 'free',
          registrationStatus: isWaitlist ? 'waitlist' : 'confirmed',
        })

        localStorage.removeItem(`registration_form_draft_${tenant?.id}`)
        setSubmitted(true)
        return
      }

      const selectedItemIds = Object.keys(selectedItems)
        .filter(k => selectedItems[parseInt(k)])
        .map(k => parseInt(k))
      
      const paymentData = await tenantApi.createPaymentIntent({
        campId: selectedCamp.id,
        selectedItems: selectedItemIds,
        email: data.email,
        childrenCount: data.children.length,
      }) as { isFree?: boolean; clientSecret?: string }

      if (paymentData.isFree || !paymentData.clientSecret) {
        await tenantApi.submitRegistration({
          ...data,
          campId: selectedCamp.id,
          selectedItems: selectedItemIds,
          totalAmount: 0,
          paymentStatus: 'free',
        })
        localStorage.removeItem(`registration_form_draft_${tenant?.id}`)
        setSubmitted(true)
        return
      }

      setClientSecret(paymentData.clientSecret)
      
      sessionStorage.setItem('registration_form_data', JSON.stringify({
        ...data,
        campId: selectedCamp.id,
        selectedItems: Object.keys(selectedItems).filter(k => selectedItems[parseInt(k)]).map(k => parseInt(k)),
        totalAmount,
      }))

      setStep('payment')
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to process registration', 'error')
    }
  }

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: tenant?.primaryColor || '#0ea5e9' }} />
      </div>
    )
  }

  if (!tenant) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Organization Not Found</h1>
          <p className="text-gray-600 dark:text-gray-400">This organization doesn't exist or has been deactivated.</p>
        </div>
      </div>
    )
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center px-4">
        <div className="card max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Registration Complete!</h2>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            Thank you for registering {fields.length} {fields.length === 1 ? 'child' : 'children'}! 
            A confirmation email has been sent.
          </p>
          {totalAmount > 0 && (
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              Payment confirmation: {currency} {totalAmount.toFixed(2)} paid
            </p>
          )}
          <Link 
            to={basePath || '/'} 
            className="btn-primary mt-6 inline-block"
            style={{ backgroundColor: tenant.primaryColor }}
          >
            Back to Home
          </Link>
        </div>
      </div>
    )
  }

  if (step === 'payment' && clientSecret && stripePromise) {
    return (
      <Elements stripe={stripePromise} options={{ clientSecret }}>
        <PaymentForm 
          tenant={tenant}
          totalAmount={totalAmount}
          childCount={fields.length}
          currency={currency}
          onSuccess={() => {
            localStorage.removeItem(`registration_form_draft_${tenant.id}`)
            setSubmitted(true)
          }}
          onBack={() => setStep('form')}
        />
      </Elements>
    )
  }

  // Unavailable camp messaging
  if (campId && !selectedCamp && unavailableCamp) {
    const { camp, reason } = unavailableCamp
    
    const messages: Record<UnavailableReason, { icon: typeof HelpCircle; title: string; message: string; color: string }> = {
      not_found: { icon: HelpCircle, title: 'Camp Not Found', message: 'The camp you\'re looking for doesn\'t exist or has been removed.', color: 'bg-gray-100 dark:bg-gray-800 text-gray-600' },
      archived: { icon: Archive, title: 'Camp Archived', message: 'This camp has been archived and is no longer available for registration.', color: 'bg-gray-100 dark:bg-gray-800 text-gray-600' },
      past: { icon: CalendarX, title: 'Camp Has Ended', message: 'This camp has already finished. Check out our upcoming camps!', color: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600' },
      paused: { icon: Pause, title: 'Registration Paused', message: camp?.waitlistMessage || 'Registration for this camp is temporarily paused. Please check back later.', color: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600' },
      closed: { icon: Lock, title: 'Registration Closed', message: 'Registration for this camp is now closed.', color: 'bg-red-100 dark:bg-red-900/30 text-red-600' },
      full: { icon: Ticket, title: 'Camp Fully Booked', message: 'This camp is fully booked and no longer accepting registrations.', color: 'bg-violet-100 dark:bg-violet-900/30 text-violet-600' },
    }
    
    const { icon: Icon, title, message, color } = messages[reason]
    
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center px-4">
        <div className="card max-w-md text-center">
          <div className={`w-16 h-16 rounded-full ${color} flex items-center justify-center mx-auto mb-4`}>
            <Icon className="w-8 h-8" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{title}</h2>
          {camp && (
            <div className="mb-4 p-4 bg-gray-100 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700">
              <h3 className="text-lg mb-1" style={{ color: tenant.primaryColor }}>{camp.name}</h3>
              <p className="text-gray-500 text-sm flex items-center gap-1.5 justify-center">
                <Calendar className="w-3.5 h-3.5" /> {camp.startDate} - {camp.endDate}
              </p>
            </div>
          )}
          <p className="text-gray-600 dark:text-gray-400 mb-6">{message}</p>
          <Link 
            to={basePath || '/'} 
            className="btn-primary inline-block"
            style={{ backgroundColor: tenant.primaryColor }}
          >
            View Available Camps
          </Link>
        </div>
      </div>
    )
  }

  if (camps.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center px-4">
        <div className="card max-w-md text-center">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">No Active Camps</h2>
          <p className="text-gray-600 dark:text-gray-400">
            There are currently no camps available for registration.
          </p>
          <Link 
            to={basePath || '/'} 
            className="btn-secondary mt-6 inline-block"
          >
            Back to Home
          </Link>
        </div>
      </div>
    )
  }

  const campPricingItems = selectedCamp 
    ? pricingItems.filter(item => item.campId === selectedCamp.id)
    : []

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/* Header */}
      <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to={basePath || '/'} className="flex items-center gap-3">
            {tenant.logoUrl ? (
              <img src={tenant.logoUrl} alt={tenant.name} className="h-10 w-auto" />
            ) : (
              <div 
                className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-bold"
                style={{ backgroundColor: tenant.primaryColor }}
              >
                {tenant.logoText || tenant.name.charAt(0)}
              </div>
            )}
            <span className="font-semibold text-gray-900 dark:text-white">{tenant.name}</span>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8">
        {/* Camp Selection */}
        {!campId && (
          <div className="card mb-6">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Select a Camp</h2>
            <div className="space-y-3">
              {camps.map((camp) => {
                const spotsLeft = camp.maxSpots - camp.spotsTaken
                const isFull = spotsLeft <= 0

                if (isFull && !camp.waitlistEnabled) return null

                return (
                  <Link
                    key={camp.id}
                    to={`${basePath}/camp/${camp.id}`}
                    className="block w-full text-left p-4 rounded border-2 transition-all border-gray-200 dark:border-gray-700 hover:border-brand-primary/50"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">{camp.name}</h3>
                      <div className="flex flex-col items-end gap-1">
                        {isFull && camp.waitlistEnabled ? (
                          <span className="text-amber-600 dark:text-amber-400 text-sm font-bold flex items-center gap-1">
                            <ClipboardList className="w-3.5 h-3.5" /> WAITLIST
                          </span>
                        ) : (
                          <span className="text-green-600 dark:text-green-400 text-sm">{spotsLeft} spots left</span>
                        )}
                      </div>
                    </div>
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-2">{camp.description}</p>
                    <div className="flex gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {camp.startDate} - {camp.endDate}</span>
                      <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Ages {camp.ageMin}-{camp.ageMax}</span>
                    </div>
                  </Link>
                )
              })}
            </div>
          </div>
        )}

        {/* Camp Info Banner */}
        {campId && selectedCamp && (
          <div className="card mb-6" style={{ backgroundColor: `${tenant.primaryColor}10`, borderColor: tenant.primaryColor }}>
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{selectedCamp.name}</h2>
                <p className="text-gray-600 dark:text-gray-400 text-sm mb-2">{selectedCamp.description}</p>
                <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                  <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {selectedCamp.startDate} - {selectedCamp.endDate}</span>
                  <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Ages {selectedCamp.ageMin}-{selectedCamp.ageMax}</span>
                  {selectedCamp.registrationStatus === 'open' && (
                    <span className="text-green-600 dark:text-green-400">{selectedCamp.maxSpots - selectedCamp.spotsTaken} spots left</span>
                  )}
                </div>
                {selectedCamp.siblingDiscountEnabled && selectedCamp.registrationStatus === 'open' && (
                  <div className="mt-3 flex items-center gap-2 px-4 py-2 bg-emerald-50 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-700 rounded-lg text-sm text-emerald-700 dark:text-emerald-400">
                    <Percent className="w-4 h-4" /> Sibling discount: 
                    {selectedCamp.siblingDiscountType === 'percentage' 
                      ? ` ${selectedCamp.siblingDiscountAmount}% off`
                      : ` ${currency} ${selectedCamp.siblingDiscountAmount} off`
                    } per additional child
                  </div>
                )}
                
                {/* Waitlist message */}
                {selectedCamp.registrationStatus === 'open' && selectedCamp.maxSpots - selectedCamp.spotsTaken <= 0 && selectedCamp.waitlistEnabled && (
                  <div className="mt-4 p-4 bg-violet-50 dark:bg-violet-900/30 border border-violet-200 dark:border-violet-700 rounded-lg">
                    <div className="flex items-center gap-2 text-violet-700 dark:text-violet-400 font-bold mb-1">
                      <ClipboardList className="w-4 h-4" /> Join Waitlist
                    </div>
                    <p className="text-purple-600 dark:text-purple-300 text-sm">
                      {selectedCamp.waitlistMessage || "This camp is currently full, but you can join the waitlist."}
                    </p>
                  </div>
                )}
              </div>
              <Link to={basePath || '/'} className="text-sm whitespace-nowrap hover:underline" style={{ color: tenant.primaryColor }}>
                ← View all camps
              </Link>
            </div>
          </div>
        )}

        {selectedCamp && (
          <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-6">
            
            {/* Children Section */}
            <div className="card">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  Children ({fields.length})
                </h2>
                <button
                  type="button"
                  onClick={() => append(defaultChild)}
                  className="btn-secondary text-sm"
                >
                  + Add Another Child
                </button>
              </div>
              
              {fields.map((field, index) => (
                <ChildSection
                  key={field.id}
                  index={index}
                  register={register}
                  errors={errors}
                  watch={watch}
                  onRemove={() => remove(index)}
                  canRemove={fields.length > 1}
                  brandColor={tenant.primaryColor}
                />
              ))}
            </div>

            {/* Parent/Guardian Information */}
            <div className="card space-y-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Parent/Guardian</h3>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Parent's Full Name *</label>
                  <input {...register('parentFullName')} className="input" />
                  {errors.parentFullName && <p className="error-message">{errors.parentFullName.message}</p>}
                </div>
                <div>
                  <label className="label">Email *</label>
                  <input type="email" {...register('email')} className="input" />
                  {errors.email && <p className="error-message">{errors.email.message}</p>}
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Phone Number *</label>
                  <input type="tel" {...register('phone')} className="input" />
                  {errors.phone && <p className="error-message">{errors.phone.message}</p>}
                </div>
                <div>
                  <label className="label">Address</label>
                  <input {...register('address')} className="input" />
                </div>
              </div>
            </div>

            {/* Emergency Contacts */}
            <div className="card space-y-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Emergency Contacts</h3>
              
              <p className="text-sm font-medium -mb-2" style={{ color: tenant.primaryColor }}>Emergency Contact 1 *</p>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="label">Name *</label>
                  <input {...register('emergency1Name')} className="input" />
                  {errors.emergency1Name && <p className="error-message">{errors.emergency1Name.message}</p>}
                </div>
                <div>
                  <label className="label">Phone *</label>
                  <input type="tel" {...register('emergency1Phone')} className="input" />
                  {errors.emergency1Phone && <p className="error-message">{errors.emergency1Phone.message}</p>}
                </div>
                <div>
                  <label className="label">Relationship *</label>
                  <input {...register('emergency1Relationship')} className="input" placeholder="e.g., Father" />
                  {errors.emergency1Relationship && <p className="error-message">{errors.emergency1Relationship.message}</p>}
                </div>
              </div>

              <p className="text-sm text-gray-500 font-medium -mb-2 pt-2">Emergency Contact 2 (optional)</p>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <label className="label">Name</label>
                  <input {...register('emergency2Name')} className="input" />
                </div>
                <div>
                  <label className="label">Phone</label>
                  <input type="tel" {...register('emergency2Phone')} className="input" />
                </div>
                <div>
                  <label className="label">Relationship</label>
                  <input {...register('emergency2Relationship')} className="input" placeholder="e.g., Mother" />
                </div>
              </div>

              <div className="pt-2">
                <label className="label">Authorised Collectors</label>
                <p className="text-gray-500 text-xs mb-2">People authorized to collect your child(ren)</p>
                <textarea {...register('authorisedCollectors')} className="input resize-none" rows={2} />
              </div>
            </div>

            {/* Pricing Items */}
            {campPricingItems.length > 0 && (
              <div className="card">
                <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Pricing Options</h3>
                <p className="text-gray-500 text-sm mb-4">
                  Prices are per child × {fields.length} {fields.length === 1 ? 'child' : 'children'}
                </p>
                <div className="space-y-2">
                  {campPricingItems.map((item) => (
                    <label
                      key={item.id}
                      className={`flex items-start gap-3 p-3 rounded border transition-all cursor-pointer ${
                        item.isRequired 
                          ? 'border-brand-primary/30 bg-brand-primary/5' 
                          : 'border-gray-200 dark:border-gray-700 hover:border-brand-primary/50'
                      }`}
                      style={item.isRequired ? { borderColor: `${tenant.primaryColor}50`, backgroundColor: `${tenant.primaryColor}10` } : {}}
                    >
                      <input
                        type="checkbox"
                        checked={!!selectedItems[item.id]}
                        disabled={item.isRequired}
                        onChange={(e) => {
                          if (!item.isRequired) {
                            setSelectedItems({ ...selectedItems, [item.id]: e.target.checked })
                          }
                        }}
                        className={`mt-1 ${item.isRequired ? 'opacity-60' : ''}`}
                      />
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 dark:text-white flex items-center gap-2">
                          {item.name}
                          {item.isRequired && (
                            <span className="text-xs px-2 py-0.5 rounded" style={{ backgroundColor: `${tenant.primaryColor}20`, color: tenant.primaryColor }}>
                              Required
                            </span>
                          )}
                        </div>
                        <div className="text-sm text-gray-500">{item.description}</div>
                      </div>
                      <div className={`font-bold ${item.amount < 0 ? 'text-green-600 dark:text-green-400' : 'text-gray-900 dark:text-white'}`}>
                        {item.amount < 0 ? '-' : ''}{currency} {Math.abs(item.amount).toFixed(2)}
                      </div>
                    </label>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 space-y-2">
                  {siblingDiscount > 0 && (
                    <>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-500">Subtotal ({fields.length} children):</span>
                        <span className="text-gray-500">{currency} {(totalAmount + siblingDiscount).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-emerald-600 dark:text-emerald-400 flex items-center gap-2">
                          <Percent className="w-3.5 h-3.5" /> Sibling Discount ({fields.length - 1} {fields.length === 2 ? 'sibling' : 'siblings'}):
                        </span>
                        <span className="text-green-600 dark:text-green-400 font-medium">
                          -{currency} {siblingDiscount.toFixed(2)}
                        </span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between items-center pt-2">
                    <span className="text-gray-900 dark:text-white font-medium">Total:</span>
                    <span className="text-2xl font-bold" style={{ color: tenant.primaryColor }}>
                      {currency} {totalAmount.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Permissions */}
            <div className="card space-y-3">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Permissions & Consent</h3>
              <p className="text-gray-500 text-sm mb-4">These permissions apply to all registered children</p>
              
              {[
                { name: 'permissionPhotos', label: 'I give permission for my child(ren) to be photographed/videoed *' },
                { name: 'permissionHealth', label: 'I authorize staff to seek emergency medical treatment if required *' },
                { name: 'permissionActivities', label: 'I give permission for my child(ren) to participate in all camp activities *' },
                { name: 'permissionLocations', label: 'I give permission for my child(ren) to visit off-site locations with supervision *' },
                { name: 'permissionMeals', label: 'I give permission for my child(ren) to have snacks and meals provided *' },
                { name: 'permissionBathroom', label: 'I give permission for my child(ren) to use bathroom facilities independently *' },
                { name: 'permissionFirstAid', label: 'I give permission for first aid to be administered if needed *' },
                { name: 'permissionEquipment', label: 'I give permission for my child(ren) to use sports equipment and facilities *' },
                { name: 'permissionAppWaiver', label: 'I acknowledge and accept the camp terms and conditions *' },
              ].map(({ name, label }) => (
                <label key={name} className="flex items-start gap-3 text-sm">
                  <input type="checkbox" {...register(name as keyof MultiChildFormData)} className="mt-1" />
                  <span className="text-gray-700 dark:text-gray-300">{label}</span>
                </label>
              ))}
              {errors.permissionAppWaiver && <p className="error-message">{errors.permissionAppWaiver.message}</p>}
            </div>

            {selectedCamp?.registrationStatus === 'paused' ? (
              <div className="bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 rounded-xl p-4 text-center">
                <span className="text-amber-700 dark:text-amber-400 font-bold flex items-center gap-2 justify-center">
                  <Pause className="w-4 h-4" /> Registration is currently paused
                </span>
              </div>
            ) : selectedCamp?.registrationStatus === 'closed' ? (
              <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-xl p-4 text-center">
                <span className="text-red-700 dark:text-red-400 font-bold flex items-center gap-2 justify-center">
                  <Lock className="w-4 h-4" /> Registration is closed
                </span>
              </div>
            ) : (
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn-primary w-full py-4 text-lg"
                style={{ backgroundColor: tenant.primaryColor }}
              >
                {isSubmitting 
                  ? 'Processing...' 
                  : selectedCamp && selectedCamp.maxSpots - selectedCamp.spotsTaken <= 0 && selectedCamp.waitlistEnabled
                    ? 'Join Waitlist'
                    : totalAmount === 0 
                      ? 'Complete Registration'
                      : `Proceed to Payment - ${currency} ${totalAmount.toFixed(2)}`
                }
              </button>
            )}
          </form>
        )}
      </main>
    </div>
  )
}

// Child Section Component
function ChildSection({
  index,
  register,
  errors,
  watch,
  onRemove,
  canRemove,
  brandColor,
}: {
  index: number
  register: ReturnType<typeof useForm<MultiChildFormData>>['register']
  errors: ReturnType<typeof useForm<MultiChildFormData>>['formState']['errors']
  watch: ReturnType<typeof useForm<MultiChildFormData>>['watch']
  onRemove: () => void
  canRemove: boolean
  brandColor: string
}) {
  const hasMedical = watch(`children.${index}.hasMedicalConditions`)
  const hasNeeds = watch(`children.${index}.hasAdditionalNeeds`)
  const hasAllergies = watch(`children.${index}.hasAllergies`)
  const hasMedication = watch(`children.${index}.hasMedication`)
  const hasFurtherInfo = watch(`children.${index}.hasFurtherInfo`)

  return (
    <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
      <div className="flex justify-between items-center mb-4">
        <h4 className="text-lg font-semibold" style={{ color: brandColor }}>
          Child {index + 1}
        </h4>
        {canRemove && (
          <button
            type="button"
            onClick={onRemove}
            className="text-red-600 dark:text-red-400 hover:text-red-500 text-sm"
          >
            Remove
          </button>
        )}
      </div>

      <div className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="label">Full Name *</label>
            <input {...register(`children.${index}.childFullName`)} className="input" />
            {errors.children?.[index]?.childFullName && (
              <p className="error-message">{errors.children[index]?.childFullName?.message}</p>
            )}
          </div>
          <div>
            <label className="label">Date of Birth *</label>
            <input type="date" {...register(`children.${index}.childDob`)} className="input" />
            {errors.children?.[index]?.childDob && (
              <p className="error-message">{errors.children[index]?.childDob?.message}</p>
            )}
          </div>
        </div>

        <div>
          <label className="label">Can walk home alone? *</label>
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-gray-900 dark:text-white text-sm">
              <input type="radio" {...register(`children.${index}.walkHomeAlone`)} value="yes" />
              Yes
            </label>
            <label className="flex items-center gap-2 text-gray-900 dark:text-white text-sm">
              <input type="radio" {...register(`children.${index}.walkHomeAlone`)} value="no" />
              No
            </label>
          </div>
        </div>

        {/* Medical Questions */}
        {[
          { field: 'hasMedicalConditions', details: 'medicalConditionsDetails', label: 'Any medical conditions?', value: hasMedical },
          { field: 'hasAdditionalNeeds', details: 'additionalNeedsDetails', label: 'Any additional needs?', value: hasNeeds },
          { field: 'hasAllergies', details: 'allergiesDetails', label: 'Any allergies?', value: hasAllergies },
          { field: 'hasMedication', details: 'medicationDetails', label: 'Taking any medication?', value: hasMedication },
          { field: 'hasFurtherInfo', details: 'furtherInfoDetails', label: 'Any further information?', value: hasFurtherInfo },
        ].map(({ field, details, label, value }) => (
          <div key={field}>
            <label className="label">{label} *</label>
            <div className="flex gap-4 mb-2">
              <label className="flex items-center gap-2 text-gray-900 dark:text-white text-sm">
                <input type="radio" {...register(`children.${index}.${field}` as any)} value="yes" />
                Yes
              </label>
              <label className="flex items-center gap-2 text-gray-900 dark:text-white text-sm">
                <input type="radio" {...register(`children.${index}.${field}` as any)} value="no" />
                No
              </label>
            </div>
            {value === 'yes' && (
              <textarea
                {...register(`children.${index}.${details}` as any)}
                className="input resize-none"
                rows={2}
                placeholder="Please provide details..."
              />
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

// Payment Form Component
function PaymentForm({ 
  tenant,
  totalAmount, 
  childCount,
  currency,
  onSuccess, 
  onBack 
}: { 
  tenant: { name: string; primaryColor: string; id: string }
  totalAmount: number
  childCount: number
  currency: string
  onSuccess: () => void
  onBack: () => void
}) {
  const stripe = useStripe()
  const elements = useElements()
  const [error, setError] = useState<string>('')
  const [processing, setProcessing] = useState(false)

  const handlePaymentSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!stripe || !elements) return

    setProcessing(true)
    setError('')

    try {
      const { error: submitError } = await elements.submit()
      if (submitError) {
        setError(submitError.message || 'Payment failed')
        setProcessing(false)
        return
      }

      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        redirect: 'if_required',
      })

      if (confirmError) {
        setError(confirmError.message || 'Payment failed')
        setProcessing(false)
        return
      }

      // Payment succeeded - submit registration
      const formData = JSON.parse(sessionStorage.getItem('registration_form_data') || '{}')
      
      await tenantApi.submitRegistration({
        ...formData,
        paymentStatus: 'paid',
      })

      sessionStorage.removeItem('registration_form_data')
      onSuccess()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Payment processing failed')
      setProcessing(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center px-4">
      <div className="card max-w-md w-full">
        <div className="mb-6">
          <button onClick={onBack} className="hover:underline text-sm" style={{ color: tenant.primaryColor }}>
            ← Back to form
          </button>
        </div>

        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">Complete Payment</h2>

        <div className="bg-gray-100 dark:bg-gray-800 p-4 rounded mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-gray-500">Registering:</span>
            <span className="text-gray-900 dark:text-white">{childCount} {childCount === 1 ? 'child' : 'children'}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-gray-500">Total Amount:</span>
            <span className="text-2xl font-bold" style={{ color: tenant.primaryColor }}>{currency} {totalAmount.toFixed(2)}</span>
          </div>
        </div>

        <form onSubmit={handlePaymentSubmit}>
          <div className="mb-6">
            <PaymentElement />
          </div>

          {error && (
            <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-600 rounded p-3 mb-4 text-red-700 dark:text-red-200">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={!stripe || processing}
            className="btn-primary w-full py-4 text-lg"
            style={{ backgroundColor: tenant.primaryColor }}
          >
            {processing ? 'Processing...' : `Pay ${currency} ${totalAmount.toFixed(2)}`}
          </button>
        </form>

        <p className="text-gray-500 text-xs text-center mt-4">
          Payments are securely processed by Stripe
        </p>
      </div>
    </div>
  )
}
